import { filterTimeslotsByMenuOrTimeRestriction } from '@features/DynamicPackagesConfigurationWizard/FormSteps/TablesStep/helpers/filterTimeslotsByMenuOrTimeRestrictions';
import { Package } from '@features/DynamicPackagesConfigurationWizard/types';
import { buildDatesToCheck } from '@features/packages/utils/buildDatesToCheck';
import { fitsPackageStayLength } from '@features/packages/utils/fitsPackageStayLength';
import {
  buildConfigGroupAvailabilityMap,
  validateSpaPackageConfigGroupAvailability,
  validateSpaPackageTimeBandAvailability,
} from '@features/packages/utils/validateSpaAvailability';
import { Rate } from '@features/rates/types';
import {
  getSpaPackageConfigGroups,
  getSpaPackageConfigGroupsKey,
} from '@features/spa/hooks/useGetSpaPackageConfigGroups';
import { getSpaPackageTimeslots } from '@features/spa/hooks/useGetSpaPackageTimeslots';
import { FIVE_MINUTES } from '@features/state/QueryClient/QueryClient';
import { getRestaurants, getRestaurantsKey } from '@features/tables/hooks/useGetRestaurants';
import {
  getTablesAvailability,
  getTablesAvailabilityKey,
} from '@features/tables/hooks/useGetTablesAvailability';
import { MappedRestaurantsType } from '@features/tables/schemas/MappedRestaurantResponse.schema';
import { TimeSlotsType as TableTimeSlotsType } from '@features/tables/schemas/TablesAvailability.schema';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { isFailedRequest } from '@utils/isFailedRequest';
import { Property } from 'shared-types/Property';
import { TimeSlotsType } from 'shared-types/SpaPackage';
import { timeBandWindow } from '@features/DynamicPackagesConfigurationWizard/helpers/spaTimeBands';
import { countSpaGuests } from '@features/spa/utils/countSpaGuests';

/**
 * Validates availability based on package type
 * - Repeating packages: At least (total days - 1) dates must be available
 * - Flexible packages: AT LEAST ONE date must be available
 */
export const validateAvailabilityByPackageType = (
  results: Array<{ date: string; isAvailable: boolean }>,
  isPackageRepeating: boolean,
  stayDuration: number
): boolean => {
  if (results.length === 0) return false;

  if (isPackageRepeating) {
    // Repeating: At least (stay duration - 1) dates must be available
    const availableCount = results.filter((result) => result.isAvailable).length;
    const minimumRequiredDays = Math.max(1, stayDuration - 1);
    return availableCount >= minimumRequiredDays;
  }

  // Flexible: AT LEAST ONE date must be available
  return results.some((result) => result.isAvailable);
};

export const PackagesAvailabilityKey = {
  packagesAvailability: ['packagesAvailability'] as const,
  getPackagesAvailability: (
    property: Property,
    rates: Rate[],
    journeyParams: ParsedJourneyParams
  ) =>
    [
      ...PackagesAvailabilityKey.packagesAvailability,
      property.id,
      rates.map((rate) => rate.id),
      journeyParams,
    ] as const,
};

export const useGetPackagesAvailability = (
  property: Property,
  rates: Rate[],
  journeyParams: ParsedJourneyParams,
  enabled = true
) => {
  const isEnabled = enabled && rates.length > 0;
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: PackagesAvailabilityKey.getPackagesAvailability(property, rates, journeyParams),
    queryFn: () => getPackagesAvailability(property, rates, journeyParams, queryClient),
    staleTime: 30000, // 30 seconds
    cacheTime: 30000, // 30 seconds
    enabled: isEnabled,
  });
};

export const getPackagesAvailability = async (
  property: Property,
  rates: Rate[],
  journeyParams: ParsedJourneyParams,
  queryClient?: ReturnType<typeof useQueryClient>
) => {
  return await Promise.all(
    rates.map(
      async (rate) =>
        await getPackagesAvailabilityForRate(property, rate, journeyParams, queryClient)
    )
  );
};

const getPackagesAvailabilityForRate = async (
  property: Property,
  rate: Rate,
  journeyParams: ParsedJourneyParams,
  queryClient?: ReturnType<typeof useQueryClient>
): Promise<{ rate: Rate; isUnavailable: boolean; isCheckFailed?: boolean } | null> => {
  const numberOfGuests = countSpaGuests(journeyParams);

  if (rate.unAvailable) return null;

  const packageData: Package | undefined = rate.package ?? undefined;

  if (!packageData) return null;

  const numberOfNights = moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days');
  const isPackageRepeating = packageData.numberOfNights === 1;

  if (!fitsPackageStayLength(packageData, numberOfNights)) {
    return { rate, isUnavailable: true };
  }

  const spaPackageId =
    packageData.meta?.entityId !== undefined ? Number(packageData.meta?.entityId) : undefined;
  const checks = await Promise.allSettled([
    unavailableUnlessRequestFailed(() =>
      checkSpaPackageAvailability(
        property.id,
        spaPackageId,
        packageData,
        journeyParams,
        numberOfGuests,
        isPackageRepeating
          ? moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days') + 1
          : packageData.numberOfDays,
        isPackageRepeating,
        queryClient
      )
    ),
    unavailableUnlessRequestFailed(() =>
      checkRestaurantsAvailability(
        property,
        packageData,
        journeyParams,
        isPackageRepeating,
        queryClient
      )
    ),
  ]);

  if (checks.some((check) => check.status === 'fulfilled' && check.value)) {
    return { rate, isUnavailable: true };
  }
  if (checks.some((check) => check.status === 'rejected')) {
    return { rate, isUnavailable: true, isCheckFailed: true };
  }
  return { rate, isUnavailable: false };
};

const unavailableUnlessRequestFailed = async (check: () => Promise<boolean>): Promise<boolean> => {
  try {
    return await check();
  } catch (error) {
    if (isFailedRequest(error)) {
      throw error;
    }
    return true;
  }
};

//returns true if unavailable, false if available
const checkSpaPackageAvailability = async (
  propertyId: number,
  spaPackageId: number | undefined,
  packageData: Package,
  journeyParams: ParsedJourneyParams,
  numberOfGuests: number,
  numberOfDays: number,
  isPackageRepeating: boolean,
  queryClient?: ReturnType<typeof useQueryClient>
): Promise<boolean> => {
  if (!spaPackageId) {
    return false;
  }

  const timeslots: TimeSlotsType = await getSpaPackageTimeslots({
    spaPackageId,
    startDate: moment(journeyParams.fromDate).format('YYYY-MM-DD'),
    quantity: numberOfGuests,
    propertyId,
    numberOfDays,
  });
  const hasTimeBandAvailability = validateSpaPackageTimeBandAvailability(
    isPackageRepeating,
    journeyParams,
    timeslots
  );
  if (!hasTimeBandAvailability) {
    return true;
  }

  const allDates = new Set<string>();
  packageData.experiences?.forEach((experience) => {
    const dates = buildDatesToCheck(
      journeyParams,
      experience.dayNumber,
      experience.dayNumberRange,
      isPackageRepeating
    );
    dates.forEach((date) => allDates.add(date));
  });

  const datesToCheck = Array.from(allDates);
  if (datesToCheck.length === 0) {
    datesToCheck.push(moment(journeyParams.fromDate).format('YYYY-MM-DD'));
  }

  const configGroupResults = await Promise.all(
    datesToCheck.map(async (date) => {
      const dayWindow = timeBandWindow({ [date]: timeslots[date] });
      if (!dayWindow) {
        return { date, configGroups: undefined };
      }
      const configGroups = await getSpaPackageConfigGroups(
        propertyId,
        spaPackageId,
        date,
        numberOfGuests,
        dayWindow.startTime,
        dayWindow.endTime
      );

      if (queryClient) {
        const queryKey = getSpaPackageConfigGroupsKey(
          propertyId,
          spaPackageId,
          date,
          numberOfGuests,
          dayWindow.startTime,
          dayWindow.endTime
        );
        queryClient.setQueryData(queryKey, configGroups);
      }

      return { date, configGroups };
    })
  );

  const configGroupAvailabilityMap = buildConfigGroupAvailabilityMap(
    configGroupResults,
    numberOfGuests
  );

  const isAvailable = validateSpaPackageConfigGroupAvailability(
    packageData,
    configGroupAvailabilityMap,
    journeyParams,
    isPackageRepeating
  );

  return !isAvailable;
};

//returns true if unavailable, false if available
const checkRestaurantsAvailability = async (
  property: Property,
  packageData: Package,
  journeyParams: ParsedJourneyParams,
  isPackageRepeating: boolean,
  queryClient?: ReturnType<typeof useQueryClient>
): Promise<boolean> => {
  if (!packageData?.restaurants || packageData.restaurants.length === 0) {
    return false;
  }

  let restaurants: MappedRestaurantsType | undefined;
  if (queryClient) {
    restaurants = await queryClient.fetchQuery({
      queryKey: getRestaurantsKey({
        propertyId: property.id,
        language: property.defaultLanguage,
      }),
      queryFn: () =>
        getRestaurants({
          propertyId: property.id,
          language: property.defaultLanguage,
        }),
      staleTime: Infinity,
      cacheTime: Infinity,
    });
  } else {
    restaurants = await getRestaurants({
      propertyId: property.id,
      language: property.defaultLanguage,
    });
  }

  if (!restaurants) {
    throw new Error('The restaurants response could not be read');
  }

  const tableTimeSlotCache = new Map<
    string,
    {
      date: string;
      timeSlots: TableTimeSlotsType;
    }
  >();

  for (const diningOption of packageData.restaurants) {
    const restaurant = restaurants.find((r) => Number(diningOption?.restaurantId) === r.config.id);

    if (!restaurant) {
      return true;
    }

    const datesToCheck = buildDatesToCheck(
      journeyParams,
      diningOption.dayNumber,
      diningOption.dayNumberRange,
      isPackageRepeating
    );

    const tablesAvailability = await Promise.all(
      datesToCheck.map(async (date) => {
        return fetchTableAvailability(
          date,
          restaurant,
          property,
          journeyParams,
          tableTimeSlotCache,
          queryClient
        );
      })
    );

    const filteredTimeSlots: { date: string; timeSlots: TableTimeSlotsType }[] =
      tablesAvailability.map(({ date, timeSlots }) => ({
        date,
        timeSlots: filterTimeslotsByMenuOrTimeRestriction(timeSlots, {
          menuIds: diningOption.menuIds,
          timeslotRestrictions: diningOption.timeRestrictions,
          id: Number(diningOption.restaurantId),
          dayNumber: diningOption.dayNumber,
          dayNumberRange: diningOption.dayNumberRange,
        }),
      }));

    const availabilityResults = filteredTimeSlots.map((slot) => ({
      date: slot.date,
      isAvailable: slot.timeSlots.length > 0,
    }));

    const stayDuration =
      moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days') + 1;

    const isAvailable = validateAvailabilityByPackageType(
      availabilityResults,
      isPackageRepeating,
      stayDuration
    );

    if (!isAvailable) {
      return true;
    }
  }

  return false;
};

const fetchTableAvailability = async (
  date: string,
  restaurant: MappedRestaurantsType[0],
  property: Property,
  journeyParams: ParsedJourneyParams,
  availabilityCache: Map<string, { date: string; timeSlots: TableTimeSlotsType }>,
  queryClient?: ReturnType<typeof useQueryClient>
) => {
  const cacheKey = `${restaurant.config.id}-${date}`;
  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;

  if (!availabilityCache.has(cacheKey)) {
    let result;
    // Use queryClient to fetch table availability from cache or fetch if not available
    if (queryClient) {
      result = await queryClient.fetchQuery({
        queryKey: getTablesAvailabilityKey({
          propertyId: property.id,
          date,
          restaurant: restaurant.restaurantName,
          restaurantId: restaurant.config.id,
          adults,
          children,
        }),
        queryFn: () =>
          getTablesAvailability({
            propertyId: property.id,
            date,
            restaurant: restaurant.restaurantName,
            restaurantId: restaurant.config.id,
            adults,
            children,
          }),
        staleTime: FIVE_MINUTES,
        cacheTime: FIVE_MINUTES,
      });
    } else {
      result = await getTablesAvailability({
        propertyId: property.id,
        date,
        restaurant: restaurant.restaurantName,
        restaurantId: restaurant.config.id,
        adults,
        children,
      });
    }

    availabilityCache.set(cacheKey, {
      date,
      timeSlots: result?.timeSlots || [],
    });
  }

  return availabilityCache.get(cacheKey) || { date, timeSlots: [] };
};

export function useInvalidatePackagesAvailability(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: (query) => query.queryKey.includes('packagesAvailability'),
  });
}
